import { format, parseISO } from 'date-fns';

import { LockedReasonEnum } from './components/LockedExpenseMessage';
import type { ApiError } from '@/api/REST/errors';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { parseLocaleNumber } from '@/utils';

type ExpenseReportParams = {
  employeeUserId: string | undefined;
  projectId: string | undefined;
  paymentMethodId: string | undefined;
  date: Date | string | undefined;
};

export const calculateExchangeRate = (
  originalAmount: number | string,
  toAmount: number | string,
) => {
  const _originalAmount =
    typeof originalAmount === 'string'
      ? parseLocaleNumber(originalAmount.trim())
      : originalAmount;

  const _toAmount =
    typeof toAmount === 'string'
      ? parseLocaleNumber(toAmount.trim())
      : toAmount;

  return (
    (_originalAmount && _toAmount > 0 ? _toAmount / _originalAmount : 0) * 100 // * 100 to have correct format
  );
};

export const generatePersonalExpenseRedirectUrl = (
  paramValues: ExpenseReportParams,
  isBillingStarterPSA: boolean,
) => {
  const urlPath = isBillingStarterPSA
    ? legacyRoutes.reports.personal.expenseOverviewReport
    : legacyRoutes.reports.personal.expenseReportSubmitPrint;

  const params = new URLSearchParams();
  Object.entries(paramValues).forEach(([key, value]) => {
    if (value !== undefined) {
      if (key === 'date') {
        const dateValue = value instanceof Date ? value : parseISO(value);
        params.set(key, format(dateValue, 'yyyy-MM-dd'));
        return;
      }

      params.set(key, value as string);
    }
  });

  return `${urlPath}?${params.toString()}`;
};

export const generateParamsForExpenseReportUrls = (
  paramValues: ExpenseReportParams,
) => {
  const params = new URLSearchParams();
  Object.entries(paramValues).forEach(([key, value]) => {
    if (value !== undefined) {
      if (key === 'date') {
        const dateValue = value instanceof Date ? value : parseISO(value);
        params.set(key, format(dateValue, 'yyyy-MM-dd'));
        return;
      }

      params.set(key, value as string);
    }
  });

  return params;
};

export const generateSpecialCaseErrorMessage = (
  error: ApiError,
): string | null => {
  if (
    error.errors.some(
      (e) =>
        !e.field &&
        /Invalid expense date .+ lies within a closed period\./.test(e.message),
    )
  ) {
    return 'The selected expense date lies within a closed accounting period. Please choose a different date.';
  }

  return null;
};

export const getFormIsLockedReasonKey = (
  isProjectInactive: boolean,
  isContractInactive: boolean,
  isApproved: boolean,
  isInvoiced: boolean,
): LockedReasonEnum | undefined => {
  if (isProjectInactive) {
    return 'project_inactive';
  }

  if (isContractInactive) {
    return 'contract_inactive';
  }

  if (isApproved && !isInvoiced) {
    return 'approved';
  }

  if (isInvoiced) {
    return 'invoiced';
  }

  return undefined;
};
